import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Download, FileSpreadsheet, Users, MapPin, CheckCircle2, Lock, Loader2, Trash2, AlertTriangle } from 'lucide-react';

const UPLOAD_STORAGE_KEY = 'bulk_upload_job';
const ALLOCATION_STORAGE_KEY = 'allocation_upload_job';

const BulkUpload = () => {
  // Password protection state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  const [parksFile, setParksFile] = useState(null);
  const [allocationFile, setAllocationFile] = useState(null);
  const [uploadingParks, setUploadingParks] = useState(false);
  const [uploadingAllocation, setUploadingAllocation] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Progress tracking state for parks
  const [uploadProgress, setUploadProgress] = useState(null);
  const [currentUploadId, setCurrentUploadId] = useState(null);
  const pollingRef = useRef(null);
  
  // Progress tracking state for allocation
  const [allocationProgress, setAllocationProgress] = useState(null);
  const [currentAllocationId, setCurrentAllocationId] = useState(null);
  const allocationPollingRef = useRef(null);
  
  // Clear data states
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearType, setClearType] = useState('');
  const [clearing, setClearing] = useState(false);
  const [clearProgress, setClearProgress] = useState(null);
  const clearPollingRef = useRef(null);
  
  const { user, userType, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // Check for existing upload jobs on page load
  useEffect(() => {
    // Check for parks upload job
    const savedJob = localStorage.getItem(UPLOAD_STORAGE_KEY);
    if (savedJob) {
      try {
        const { uploadId, type } = JSON.parse(savedJob);
        if (uploadId && type === 'parks') {
          setCurrentUploadId(uploadId);
          setUploadingParks(true);
          pollUploadProgress(uploadId);
          pollingRef.current = setInterval(() => {
            pollUploadProgress(uploadId);
          }, 2000);
        }
      } catch (e) {
        localStorage.removeItem(UPLOAD_STORAGE_KEY);
      }
    }
    
    // Check for allocation upload job
    const savedAllocationJob = localStorage.getItem(ALLOCATION_STORAGE_KEY);
    if (savedAllocationJob) {
      try {
        const { uploadId } = JSON.parse(savedAllocationJob);
        if (uploadId) {
          setCurrentAllocationId(uploadId);
          setUploadingAllocation(true);
          pollAllocationProgress(uploadId);
          allocationPollingRef.current = setInterval(() => {
            pollAllocationProgress(uploadId);
          }, 2000);
        }
      } catch (e) {
        localStorage.removeItem(ALLOCATION_STORAGE_KEY);
      }
    }
  }, []);
  
  // Polling function for parks background uploads
  const pollUploadProgress = useCallback(async (uploadId) => {
    try {
      const response = await adminAPI.getUploadProgress(uploadId);
      const progressData = response.data;
      
      setUploadProgress(progressData);
      
      if (progressData.status === 'completed') {
        // Stop polling and clear storage
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        localStorage.removeItem(UPLOAD_STORAGE_KEY);
        setUploadingParks(false);
        setCurrentUploadId(null);
        setUploadResult({
          message: `Successfully processed ${progressData.parks_created} parks with ${progressData.employees_added} employees`,
          total_parks: progressData.parks_created,
          total_employees: progressData.employees_added,
          total_rows: progressData.total_rows
        });
        toast.success(`Upload complete: ${progressData.parks_created} parks processed`);
        setParksFile(null);
        const fileInput = document.getElementById('parks-file-input');
        if (fileInput) fileInput.value = '';
      } else if (progressData.status === 'error') {
        // Stop polling on error and clear storage
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        localStorage.removeItem(UPLOAD_STORAGE_KEY);
        setUploadingParks(false);
        setCurrentUploadId(null);
        toast.error(progressData.error_message || 'Upload failed');
        setUploadResult({
          message: 'Upload failed',
          errors: [progressData.error_message || 'Unknown error']
        });
      } else if (progressData.status === 'not_found') {
        // Job expired or server restarted
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        localStorage.removeItem(UPLOAD_STORAGE_KEY);
        setUploadingParks(false);
        setCurrentUploadId(null);
        setUploadProgress(null);
        toast.info('Previous upload session expired. Please upload again.');
      }
      // Otherwise keep polling (status === 'processing')
    } catch (error) {
      console.error('Error polling progress:', error);
    }
  }, []);
  
  // Polling function for allocation background uploads
  const pollAllocationProgress = useCallback(async (uploadId) => {
    try {
      const response = await adminAPI.getUploadProgress(uploadId);
      const progressData = response.data;
      
      setAllocationProgress(progressData);
      
      if (progressData.status === 'completed') {
        if (allocationPollingRef.current) {
          clearInterval(allocationPollingRef.current);
          allocationPollingRef.current = null;
        }
        localStorage.removeItem(ALLOCATION_STORAGE_KEY);
        setUploadingAllocation(false);
        setCurrentAllocationId(null);
        setUploadResult({
          message: `Updated ${progressData.parks_updated} park assignments, created ${progressData.supervisors_created} new supervisors`,
          parks_updated: progressData.parks_updated,
          supervisors_created: progressData.supervisors_created,
          total_supervisors: progressData.total_supervisors,
          errors: progressData.errors || []
        });
        toast.success(`Allocation complete: ${progressData.parks_updated} parks assigned`);
        setAllocationFile(null);
        const fileInput = document.getElementById('allocation-file-input');
        if (fileInput) fileInput.value = '';
      } else if (progressData.status === 'error') {
        if (allocationPollingRef.current) {
          clearInterval(allocationPollingRef.current);
          allocationPollingRef.current = null;
        }
        localStorage.removeItem(ALLOCATION_STORAGE_KEY);
        setUploadingAllocation(false);
        setCurrentAllocationId(null);
        toast.error(progressData.error_message || 'Allocation failed');
        setUploadResult({
          message: 'Allocation failed',
          errors: [progressData.error_message || 'Unknown error']
        });
      } else if (progressData.status === 'not_found') {
        if (allocationPollingRef.current) {
          clearInterval(allocationPollingRef.current);
          allocationPollingRef.current = null;
        }
        localStorage.removeItem(ALLOCATION_STORAGE_KEY);
        setUploadingAllocation(false);
        setCurrentAllocationId(null);
        setAllocationProgress(null);
        toast.info('Previous allocation session expired. Please upload again.');
      }
    } catch (error) {
      console.error('Error polling allocation progress:', error);
    }
  }, []);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      if (allocationPollingRef.current) {
        clearInterval(allocationPollingRef.current);
      }
    };
  }, []);
  
  // Auth check
  useEffect(() => {
    if (authLoading) return;
    
    if (!user || userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [user, userType, navigate, authLoading]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      await adminAPI.verifyProtectedPassword(password);
      setIsAuthenticated(true);
      toast.success('Access granted');
    } catch (error) {
      toast.error('Invalid password');
      setPassword('');
    } finally {
      setVerifying(false);
    }
  };

  const handleDownloadTemplate = async (type) => {
    try {
      const response = type === 'parks' 
        ? await adminAPI.downloadParksTemplate()
        : await adminAPI.downloadEmployeesTemplate();
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'parks' ? 'parks_employees_template.xlsx' : 'supervisor_allocation_template.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleUploadParks = async () => {
    if (!parksFile) {
      toast.error('Please select a file');
      return;
    }

    setUploadingParks(true);
    setUploadResult(null);
    setUploadProgress(null);
    
    try {
      const response = await adminAPI.uploadParks(parksFile);
      
      // Check if it's a background job (large file)
      if (response.data.upload_id && response.data.status === 'processing') {
        // Save to localStorage so we can resume if page is revisited
        localStorage.setItem(UPLOAD_STORAGE_KEY, JSON.stringify({
          uploadId: response.data.upload_id,
          type: 'parks',
          startedAt: new Date().toISOString()
        }));
        
        // Start polling for progress
        setCurrentUploadId(response.data.upload_id);
        setUploadProgress({
          status: 'processing',
          total_rows: response.data.total_rows,
          processed_rows: 0,
          parks_created: 0,
          employees_added: 0
        });
        
        toast.info(`Processing ${response.data.total_rows} rows in background. You can navigate away and come back.`);
        
        // Poll every 2 seconds
        pollingRef.current = setInterval(() => {
          pollUploadProgress(response.data.upload_id);
        }, 2000);
        
        // Also poll immediately
        pollUploadProgress(response.data.upload_id);
      } else {
        // Synchronous processing completed
        localStorage.removeItem(UPLOAD_STORAGE_KEY);
        setUploadResult(response.data);
        toast.success(response.data.message || `Successfully uploaded parks`);
        setParksFile(null);
        setUploadingParks(false);
        // Reset file input
        document.getElementById('parks-file-input').value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploadingParks(false);
    }
  };

  const handleUploadAllocation = async () => {
    if (!allocationFile) {
      toast.error('Please select a file');
      return;
    }

    setUploadingAllocation(true);
    setUploadResult(null);
    setAllocationProgress(null);
    
    try {
      const response = await adminAPI.uploadEmployees(allocationFile);
      
      // Check if it's a background job (large file)
      if (response.data.upload_id && response.data.status === 'processing') {
        // Save to localStorage for resume
        localStorage.setItem(ALLOCATION_STORAGE_KEY, JSON.stringify({
          uploadId: response.data.upload_id,
          type: 'allocation',
          startedAt: new Date().toISOString()
        }));
        
        setCurrentAllocationId(response.data.upload_id);
        setAllocationProgress({
          status: 'processing',
          total_rows: response.data.total_rows,
          processed_rows: 0,
          parks_updated: 0,
          supervisors_created: 0
        });
        
        toast.info(`Processing ${response.data.total_rows} rows in background. You can navigate away and come back.`);
        
        // Poll every 2 seconds
        allocationPollingRef.current = setInterval(() => {
          pollAllocationProgress(response.data.upload_id);
        }, 2000);
        
        // Poll immediately
        pollAllocationProgress(response.data.upload_id);
      } else {
        // Synchronous processing completed
        localStorage.removeItem(ALLOCATION_STORAGE_KEY);
        setUploadResult(response.data);
        toast.success(response.data.message || `Successfully processed allocation`);
        setAllocationFile(null);
        setUploadingAllocation(false);
        document.getElementById('allocation-file-input').value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
      setUploadingAllocation(false);
    }
  };

  // Clear data handler
  const handleClearData = async () => {
    if (!clearType) return;
    
    setClearing(true);
    setClearProgress(null);
    
    try {
      let response;
      if (clearType === 'all') {
        response = await adminAPI.clearAllData();
      } else {
        response = await adminAPI.clearSpecificData(clearType);
      }
      
      if (response.data.job_id) {
        const jobId = response.data.job_id;
        setClearProgress({
          status: 'processing',
          deleted_count: 0
        });
        
        clearPollingRef.current = setInterval(async () => {
          try {
            const progressRes = await adminAPI.getClearProgress(jobId);
            setClearProgress(progressRes.data);
            
            if (progressRes.data.status === 'completed') {
              clearInterval(clearPollingRef.current);
              clearPollingRef.current = null;
              setClearing(false);
              toast.success(`Successfully cleared ${clearType} data`);
              setTimeout(() => {
                setShowClearModal(false);
                setClearProgress(null);
                setClearType('');
              }, 2000);
            } else if (progressRes.data.status === 'error') {
              clearInterval(clearPollingRef.current);
              clearPollingRef.current = null;
              setClearing(false);
              toast.error(progressRes.data.error_message || 'Failed to clear data');
            }
          } catch (e) {
            console.error('Error polling clear progress:', e);
          }
        }, 1000);
      }
    } catch (error) {
      toast.error('Failed to clear data');
      setClearing(false);
    }
  };

  // Cleanup clear polling on unmount
  useEffect(() => {
    return () => {
      if (clearPollingRef.current) {
        clearInterval(clearPollingRef.current);
      }
    };
  }, []);

  // Password Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-md overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
            <div className="flex items-center gap-3">
              <Lock className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-bold">Excel Data Import</h1>
                <p className="text-blue-200 text-sm">Protected Area</p>
              </div>
            </div>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-2">Enter Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Access password"
                className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:ring-0"
                data-testid="bulk-upload-password-input"
                autoFocus
              />
            </div>
            
            <button
              type="submit"
              disabled={!password || verifying}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center justify-center gap-2"
              data-testid="bulk-upload-password-submit"
            >
              {verifying ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  Access Excel Import
                </>
              )}
            </button>
            
            <button
              type="button"
              onClick={() => navigate('/admin/dashboard')}
              className="w-full h-10 text-slate-600 hover:text-slate-800 font-semibold"
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white p-4 shadow-md">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Link to="/admin/dashboard" className="hover:opacity-80">
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Excel Data Import
            </h1>
            <p className="text-sm opacity-90">Upload master data via Excel files</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Step indicator */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <h3 className="font-bold text-slate-700 mb-3">Upload Steps:</h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">1</div>
              <span className="text-sm font-medium">Parks & Employees</span>
            </div>
            <div className="flex-1 h-1 bg-slate-200"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">2</div>
              <span className="text-sm font-medium">Supervisor Allocation</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Excel 1: Parks & Employees */}
          <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-blue-900">Step 1: Parks & Employees</h2>
                <p className="text-xs text-blue-600">Upload this first</p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-semibold text-blue-800 mb-2">Your Excel format:</p>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• <strong>Zone</strong> - Zone name</li>
                <li>• <strong>Division</strong> - Division name</li>
                <li>• <strong>Park ID</strong> - Unique park number</li>
                <li>• <strong>Name of the Park</strong> - Park name</li>
                <li>• <strong>Name of the Employee</strong> - Employee name</li>
                <li>• <strong>Category</strong> - Employee category (optional)</li>
                <li>• <strong>Aadhar Number</strong> - (optional)</li>
                <li>• <strong>Mobile Number</strong> - (optional)</li>
                <li>• <strong>Google Maps Link</strong> - Location URL (optional)</li>
              </ul>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Select Excel File
              </label>
              <input
                id="parks-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setParksFile(e.target.files[0])}
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-blue-600 focus:ring-0 text-sm"
              />
              {parksFile && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  {parksFile.name}
                </p>
              )}
            </div>

            <button
              onClick={handleUploadParks}
              disabled={!parksFile || uploadingParks}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upload-parks-btn"
            >
              {uploadingParks ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload Parks & Employees
                </>
              )}
            </button>
            
            {/* Progress Indicator */}
            {uploadingParks && uploadProgress && (
              <div className="mt-4 bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-blue-800">
                    {uploadProgress.status === 'processing' ? 'Processing...' : 'Completing...'}
                  </span>
                  <span className="text-sm text-blue-600">
                    {uploadProgress.processed_rows || 0} / {uploadProgress.total_rows || 0} rows
                  </span>
                </div>
                <div className="w-full h-3 bg-blue-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${uploadProgress.total_rows > 0 
                        ? Math.round((uploadProgress.processed_rows / uploadProgress.total_rows) * 100) 
                        : 0}%` 
                    }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-blue-700">
                  <span>Parks: {uploadProgress.parks_created || 0}</span>
                  <span>Employees: {uploadProgress.employees_added || 0}</span>
                </div>
                <p className="text-xs text-blue-600 mt-2 text-center">
                  Large files are processed in the background. You can wait here or check back later.
                </p>
              </div>
            )}
          </div>

          {/* Excel 2: Supervisor Allocation */}
          <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-green-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-900">Step 2: Supervisor Allocation</h2>
                <p className="text-xs text-green-600">Upload after Step 1</p>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-semibold text-green-800 mb-2">Your Excel format:</p>
              <ul className="text-xs text-green-700 space-y-1">
                <li>• <strong>Unique number for park</strong> - Park ID</li>
                <li>• <strong>Name of the park</strong> - Park name</li>
                <li>• <strong>Name of the supervisor allotted</strong> - Supervisor name</li>
                <li>• <strong>ID number</strong> - Supervisor ID</li>
              </ul>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold text-slate-700 mb-2">
                Select Excel File
              </label>
              <input
                id="allocation-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setAllocationFile(e.target.files[0])}
                className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:border-green-600 focus:ring-0 text-sm"
              />
              {allocationFile && (
                <p className="text-sm text-green-600 mt-2 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  {allocationFile.name}
                </p>
              )}
            </div>

            <button
              onClick={handleUploadAllocation}
              disabled={!allocationFile || uploadingAllocation}
              className="w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="upload-allocation-btn"
            >
              {uploadingAllocation ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload Supervisor Allocation
                </>
              )}
            </button>
            
            {/* Progress Indicator for Allocation */}
            {uploadingAllocation && allocationProgress && (
              <div className="mt-4 bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-green-800">
                    {allocationProgress.status === 'processing' ? 'Processing...' : 'Completing...'}
                  </span>
                  <span className="text-sm text-green-600">
                    {allocationProgress.processed_rows || 0} / {allocationProgress.total_rows || 0} rows
                  </span>
                </div>
                <div className="w-full h-3 bg-green-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-600 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${allocationProgress.total_rows > 0 
                        ? Math.round((allocationProgress.processed_rows / allocationProgress.total_rows) * 100) 
                        : 0}%` 
                    }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-green-700">
                  <span>Parks Updated: {allocationProgress.parks_updated || 0}</span>
                  <span>New Supervisors: {allocationProgress.supervisors_created || 0}</span>
                </div>
                <p className="text-xs text-green-600 mt-2 text-center">
                  Large files are processed in the background. You can wait here or check back later.
                </p>
              </div>
            )}

            <p className="text-xs text-orange-600 mt-3 bg-orange-50 p-2 rounded">
              ⚠️ New supervisors will be created automatically with default PIN: <strong>1234</strong>
            </p>
          </div>
        </div>

        {/* Upload Result */}
        {uploadResult && (
          <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              Upload Results
            </h3>
            
            {uploadResult.message && (
              <div className="bg-green-50 text-green-800 p-4 rounded-lg mb-4 font-medium">
                {uploadResult.message}
              </div>
            )}
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              {uploadResult.total_parks !== undefined && (
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-700">{uploadResult.total_parks}</div>
                  <div className="text-sm text-blue-600">Parks</div>
                </div>
              )}
              {uploadResult.total_employees !== undefined && (
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-700">{uploadResult.total_employees}</div>
                  <div className="text-sm text-green-600">Employees</div>
                </div>
              )}
              {uploadResult.parks_updated !== undefined && (
                <div className="bg-purple-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-purple-700">{uploadResult.parks_updated}</div>
                  <div className="text-sm text-purple-600">Parks Assigned</div>
                </div>
              )}
              {uploadResult.supervisors_created !== undefined && (
                <div className="bg-orange-50 p-4 rounded-lg text-center">
                  <div className="text-2xl font-bold text-orange-700">{uploadResult.supervisors_created}</div>
                  <div className="text-sm text-orange-600">New Supervisors</div>
                </div>
              )}
            </div>

            {uploadResult.note && (
              <div className="bg-yellow-50 text-yellow-800 p-3 rounded-lg text-sm mb-4">
                💡 {uploadResult.note}
              </div>
            )}

            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div>
                <h4 className="font-bold text-red-700 mb-2">Errors ({uploadResult.errors.length})</h4>
                <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                  {uploadResult.errors.map((error, idx) => (
                    <div key={idx} className="text-sm text-red-700 mb-1">• {error}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Clear Data Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border-2 border-red-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-700">Clear Data</h2>
              <p className="text-sm text-slate-600">Delete specific or all data from the system</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <button
              onClick={() => { setClearType('parks'); setShowClearModal(true); }}
              className="p-4 border-2 border-red-200 rounded-lg hover:border-red-400 hover:bg-red-50 text-center"
              data-testid="clear-parks-btn"
            >
              <MapPin className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <span className="text-sm font-bold">Clear Parks</span>
            </button>
            
            <button
              onClick={() => { setClearType('supervisors'); setShowClearModal(true); }}
              className="p-4 border-2 border-red-200 rounded-lg hover:border-red-400 hover:bg-red-50 text-center"
              data-testid="clear-supervisors-btn"
            >
              <Users className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <span className="text-sm font-bold">Clear Supervisors</span>
            </button>
            
            <button
              onClick={() => { setClearType('attendance'); setShowClearModal(true); }}
              className="p-4 border-2 border-red-200 rounded-lg hover:border-red-400 hover:bg-red-50 text-center"
              data-testid="clear-attendance-btn"
            >
              <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <span className="text-sm font-bold">Clear Attendance</span>
            </button>
            
            <button
              onClick={() => { setClearType('inspections'); setShowClearModal(true); }}
              className="p-4 border-2 border-red-200 rounded-lg hover:border-red-400 hover:bg-red-50 text-center"
              data-testid="clear-inspections-btn"
            >
              <FileSpreadsheet className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <span className="text-sm font-bold">Clear Inspections</span>
            </button>
            
            <button
              onClick={() => { setClearType('all'); setShowClearModal(true); }}
              className="p-4 border-2 border-red-600 bg-red-50 rounded-lg hover:bg-red-100 text-center"
              data-testid="clear-all-btn"
            >
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <span className="text-sm font-bold text-red-700">Clear ALL</span>
            </button>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-slate-100 rounded-lg p-6">
          <h3 className="font-bold text-slate-800 mb-3">📋 How to Use</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
            <li><strong>Step 1:</strong> Upload your Parks & Employees Excel file first (creates parks with their assigned workers)</li>
            <li><strong>Step 2:</strong> Upload Supervisor Allocation Excel file (assigns supervisors to parks and creates new supervisor accounts)</li>
            <li>Check the results after each upload for any errors</li>
            <li>New supervisors will have default PIN <strong>1234</strong> - ask them to change it on first login</li>
          </ol>
        </div>
      </div>

      {/* Clear Data Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="bg-red-600 text-white p-4 flex items-center justify-between rounded-t-xl">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Clear {clearType === 'all' ? 'All Data' : clearType.charAt(0).toUpperCase() + clearType.slice(1)}
              </h3>
              <button 
                onClick={() => !clearing && (setShowClearModal(false), setClearType(''), setClearProgress(null))} 
                disabled={clearing}
                className="p-2 hover:bg-white/20 rounded-lg disabled:opacity-50"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4">
              {clearProgress ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-slate-700">
                      {clearProgress.status === 'completed' ? 'Completed!' : 'Deleting...'}
                    </span>
                    <span className="text-sm text-slate-500">
                      {clearProgress.deleted_count || clearProgress.deleted?.total || 0} items deleted
                    </span>
                  </div>
                  <div className="w-full h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${clearProgress.status === 'completed' ? 'bg-green-600 w-full' : 'bg-red-600 w-3/4 animate-pulse'}`}
                    />
                  </div>
                  {clearProgress.status === 'completed' && (
                    <p className="text-green-600 font-bold text-center">Data cleared successfully!</p>
                  )}
                </div>
              ) : (
                <>
                  <p className="text-slate-700 mb-4">
                    This will permanently delete {clearType === 'all' ? (
                      <strong>ALL data</strong>
                    ) : (
                      <strong>all {clearType}</strong>
                    )} from the system.
                  </p>
                  {clearType === 'all' && (
                    <ul className="list-disc list-inside text-red-700 space-y-1 mb-4">
                      <li>All Parks</li>
                      <li>All Supervisors</li>
                      <li>All Inspections</li>
                      <li>All Attendance Records</li>
                    </ul>
                  )}
                  <p className="text-red-600 font-bold">This action cannot be undone!</p>
                </>
              )}
            </div>
            
            {!clearProgress && (
              <div className="border-t p-4 flex gap-3">
                <button 
                  onClick={handleClearData} 
                  disabled={clearing}
                  className="flex-1 h-12 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50"
                >
                  {clearing ? 'Clearing...' : `Yes, Clear ${clearType === 'all' ? 'All' : clearType}`}
                </button>
                <button 
                  onClick={() => (setShowClearModal(false), setClearType(''))}
                  className="h-12 px-6 bg-slate-200 text-slate-700 rounded-lg font-bold hover:bg-slate-300"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkUpload;
